import { createApiReadCoordinator } from "./apiReadCoordinator";
import { holdManualRefresh } from "./manualRefresh";

export const SESSION_EXPIRED_EVENT = "pharmhub:session-expired";

let installed = false;
let sessionEpoch = 0;
let readScope: string | null = null;
let coordinator: ReturnType<typeof createApiReadCoordinator> | undefined;

// This is a browser-session boundary, not a substitute for server warehouse ACL.
export function setApiReadScope(scope: string | null): void {
  sessionEpoch += 1;
  readScope = scope;
  coordinator?.setScope(scope);
}

export function invalidateApiReads(): void {
  coordinator?.invalidate();
}

function apiPath(input: RequestInfo | URL): string | null {
  try {
    const raw = input instanceof Request ? input.url : String(input);
    const url = new URL(raw, window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.startsWith("/api/")) return null;
    return url.pathname;
  } catch {
    return null;
  }
}

/**
 * Install one fail-closed session observer for every API request.
 *
 * Feature components still own their local loading/error state, while the
 * application shell consistently returns to login when any protected request
 * proves that the server session has expired.
 */
export function installApiSessionObserver(): void {
  if (installed) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  coordinator = createApiReadCoordinator(originalFetch, window.location.origin);
  coordinator.setScope(readScope);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const path = apiPath(input);
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    const mutation = Boolean(path && method !== "GET" && method !== "HEAD");
    if (mutation) invalidateApiReads();
    const releaseRefreshHold = mutation ? holdManualRefresh() : undefined;
    const requestEpoch = sessionEpoch;
    let response: Response;
    try { response = await coordinator!.read(input, init); }
    finally {
      if (mutation) invalidateApiReads();
      releaseRefreshHold?.();
    }
    if (requestEpoch === sessionEpoch && response.status === 401 && path && path !== "/api/v1/auth/login") {
      setApiReadScope(null);
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
    if (response.status === 403 && path) invalidateApiReads();
    return response;
  };
}
